import { useApolloClient } from "@apollo/client";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { AttachmentInput } from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { FilesBox } from "../../../commons/files/FilesBox.js";
import { ProductOverview__ProductFragment } from "../Product.generated.js";
import {
  useAddProductAssetsMutation,
  useRemoveProductAssetMutation,
} from "./ProductOverviewFilesBox.generated.js";
import { useProductAssetUpload } from "../useProductAssetUpload.js";

export function ProductOverviewFilesBox({
  product,
  isEditable,
  refetchQueriesOnAction,
}: {
  product: ProductOverview__ProductFragment;
  isEditable?: boolean;
  refetchQueriesOnAction?: string[];
}) {
  const attachments = product.attachments
    .filter(attachment => attachment.group !== "3dItemDefinition")
    .map(attachment => ({
      ...attachment,
      id: attachment.id.toString(),
    }));

  const { getUploadUrl, addAssets, removeAsset, loading } =
    useProductAttachments(product.id, refetchQueriesOnAction);

  return (
    <FilesBox
      attachments={attachments}
      readOnly={!isEditable}
      handleAdd={async files => {
        const attachments = await Promise.all(
          files.map(async file => {
            const uploadUrl = await getUploadUrl({
              filename: file.name,
              contentType: file.type,
            });

            await fetch(uploadUrl, {
              method: "PUT",
              body: file,
            })
              .then(response => {
                if (!response.ok) {
                  throw new Error(
                    `Failed to upload file: ${response.statusText}`
                  );
                }
              })
              .catch(err => {
                console.error("File upload failed", err);
                throw err;
              });

            return {
              title: file.name,
              url: uploadUrl,
              mimeType: file.type,
            };
          })
        );

        await addAssets(attachments);
      }}
      handleDelete={removeAsset}
      loading={loading}
      renderAddButton={button => (
        <RestrictedByCapabilityWithDebug capability="pim">
          <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
            {button}
          </RestrictedByOrganisationPermissionWithDebug>
        </RestrictedByCapabilityWithDebug>
      )}
      renderDeleteButton={button => (
        <RestrictedByCapabilityWithDebug capability="pim">
          <RestrictedByOrganisationPermissionWithDebug permission="pim_catalogue_update">
            {button}
          </RestrictedByOrganisationPermissionWithDebug>
        </RestrictedByCapabilityWithDebug>
      )}
    />
  );
}

const useProductAttachments = (
  productId: string,
  refetchQueries?: string[]
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["FilesBoxTable"]);

  const { getUploadUrl, loading: loadingGetUploadUrl } =
    useProductAssetUpload();

  const client = useApolloClient();
  const [add, { loading: loadingAdd }] = useAddProductAssetsMutation({
    client,
  });
  const [remove, { loading: loadingRemove }] = useRemoveProductAssetMutation({
    client,
  });

  const addAssets = React.useCallback(
    async (assets: Array<AttachmentInput>) => {
      try {
        const r = await add({
          variables: {
            assets,
            productId,
          },
          ...(refetchQueries
            ? {
                refetchQueries: refetchQueries,
                awaitRefetchQueries: true,
              }
            : undefined),
        });

        if (!refetchQueries) await client.reFetchObservableQueries();

        enqueueSnackbar(
          assets.length > 1 ? t("Files uploaded") : t("File uploaded")
        );

        return r.data?.addProductAssets.assets;
      } catch (e) {
        if (e instanceof Error)
          enqueueSnackbar(e.message, { variant: "error" });
      }
    },
    [add, productId, refetchQueries, client, enqueueSnackbar, t]
  );

  const removeAsset = React.useCallback(
    async (assetId: string) => {
      try {
        const r = await remove({
          variables: {
            assetId: Number(assetId),
            productId,
          },
          ...(refetchQueries
            ? {
                refetchQueries: refetchQueries,
                awaitRefetchQueries: true,
              }
            : undefined),
        });

        if (!refetchQueries) await client.reFetchObservableQueries();

        enqueueSnackbar(t("File deleted"));

        return r.data?.removeProductAssets.ok;
      } catch (e) {
        if (e instanceof Error)
          enqueueSnackbar(e.message, { variant: "error" });
      }
    },
    [remove, productId, refetchQueries, client, enqueueSnackbar, t]
  );

  return {
    getUploadUrl,
    addAssets,
    removeAsset,
    loading: loadingAdd || loadingRemove || loadingGetUploadUrl,
  };
};
